import { useRef } from "react";

/**
 * A drag handle on a panel's edge.
 *
 * The properties panel is a fixed 296px, which is the width at which a binding
 * expression, a colour swatch and a unit suffix all have to share one row. The
 * fields now stack rather than truncate, but some content — a long expression,
 * a table's column list — is simply better with more room, and how much room
 * that deserves is the user's call and nobody else's.
 *
 * Keyboard users get the same control: the handle is focusable and the arrow
 * keys move it, which is the part that usually gets left out.
 */
export function PanelResizer({ side, width, min, max, onChange, label }: { side: "left" | "right"; width: number; min: number; max: number; onChange: (width: number) => void; label: string }) {
  const start = useRef<{ x: number; width: number } | null>(null);
  const clamp = (w: number) => Math.round(Math.min(max, Math.max(min, w)));

  return (
    <div
      className={`bpe-resizer bpe-resizer--${side}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={(e) => {
        start.current = { x: e.clientX, width };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const delta = e.clientX - start.current.x;
        onChange(clamp(start.current.width + (side === "right" ? -delta : delta)));
      }}
      onPointerUp={(e) => {
        start.current = null;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 40 : 8;
        if (e.key === "ArrowLeft") onChange(clamp(width + (side === "right" ? step : -step)));
        else if (e.key === "ArrowRight") onChange(clamp(width + (side === "right" ? -step : step)));
        else return;
        e.preventDefault();
      }}
      onDoubleClick={() => onChange(clamp(side === "right" ? 296 : 272))}
    />
  );
}
